import axios from "axios";
import { clearSession, getSessionToken } from "@/lib/auth";

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

const publicApiClient = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

apiClient.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      clearSession();
      if (window.location.pathname !== "/login") {
        window.location.href = `/login?redirect=${encodeURIComponent(window.location.pathname)}`;
      }
    }
    return Promise.reject(error);
  }
);

export async function login(email, password) {
  const response = await apiClient.post("/auth/login", { email, password });
  return response.data;
}

export async function exchangeSharedSignIn(accessToken) {
  const response = await publicApiClient.post("/auth/sso/exchange", {
    access_token: accessToken,
  });
  return response.data;
}

export async function fetchCurrentUser() {
  const response = await apiClient.get("/auth/me");
  return response.data;
}

export async function logout() {
  await apiClient.post("/auth/logout");
  clearSession();
}

export async function fetchEvent(eventId) {
  const response = await apiClient.get(`/events/${encodeURIComponent(eventId)}`, { timeout: 8000 });
  return response.data;
}

export async function fetchQueueState(eventId) {
  const response = await apiClient.get(`/events/${encodeURIComponent(eventId)}`, { params: { queueSync: "state" }, timeout: 5000 });
  return response.data;
}

export async function fetchConnectedDevices(eventId) {
  const response = await apiClient.get(`/events/${encodeURIComponent(eventId)}`, { params: { devices: "1" }, timeout: 5000 });
  return response.data;
}

export async function fetchQueueRevision(eventId) {
  const response = await apiClient.get(`/events/${encodeURIComponent(eventId)}`, { params: { queueSync: "revision" }, timeout: 3000 });
  return response.data;
}

function preparationEnvelope(baseRevision) {
  const id = () => Array.from(crypto.getRandomValues(new Uint8Array(16)), b => b.toString(16).padStart(2, "0")).join("");
  return { baseRevision, operationId: id(), deviceId: id() };
}

export async function fetchEvents() {
  const response = await apiClient.get("/events");
  return response.data.events || [];
}

export async function createEvent(payload) {
  const response = await apiClient.post("/events", payload);
  return response.data;
}

export async function saveEvent(eventId, payload) {
  const response = await apiClient.put(
    `/events/${encodeURIComponent(eventId)}`,
    payload
  );
  return response.data;
}

export async function archiveEvent(eventId) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/archive`
  );
  return response.data;
}

export async function updateViews(eventId, payload) {
  const response = await apiClient.put(
    `/events/${encodeURIComponent(eventId)}/views`,
    payload
  );
  return response.data;
}

export async function downloadEventExport(eventId, exportName) {
  const response = await apiClient.get(
    `/events/${encodeURIComponent(eventId)}/exports/${exportName}`,
    { responseType: "blob" }
  );
  return response.data;
}

export async function downloadCustomEventExport(eventId, payload) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/exports/custom.csv`,
    payload,
    { responseType: "blob" }
  );
  return response.data;
}

export async function startAudioExportJob(eventId) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/exports/audio/jobs`
  );
  return response.data;
}

export async function fetchAudioExportJob(eventId, jobId) {
  const response = await apiClient.get(
    `/events/${encodeURIComponent(eventId)}/exports/audio/jobs/${encodeURIComponent(jobId)}`
  );
  return response.data;
}

export async function previewImport(eventId, payload) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/import-preview`,
    payload
  );
  return response.data;
}

export async function commitImport(eventId, items, baseRevision) {
  const envelope = preparationEnvelope(baseRevision);
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/import-commit`,
    { items, ...envelope }
  );
  return response.data;
}

export async function updateParticipants(eventId, payload) {
  const response = await apiClient.put(
    `/events/${encodeURIComponent(eventId)}/participants`,
    payload
  );
  return response.data;
}

export async function updateParticipantRow(eventId, participantId, payload) {
  const response = await apiClient.patch(
    `/events/${encodeURIComponent(eventId)}/participants/${encodeURIComponent(participantId)}`,
    payload
  );
  return response.data;
}

export async function fetchEventStudents(eventId) {
  const response = await apiClient.get(
    `/events/${encodeURIComponent(eventId)}/students`
  );
  return response.data;
}

export async function syncEventStudents(eventId) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/students/sync`
  );
  return response.data;
}

export async function fetchStudentPronunciationAudioUrl(eventId, studentId, source = "active") {
  const response = await apiClient.get(
    `/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation-audio-url`,
    { params: { source } }
  );
  return response.data;
}

export async function approveStudentPronunciation(eventId, studentId) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation/approve`
  );
  return response.data.student;
}

export async function rejectStudentPronunciation(eventId, studentId) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation/reject`
  );
  return response.data.student;
}

export async function resetStudentPronunciation(eventId, studentId) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation/reset`
  );
  return response.data.student;
}

export async function fetchStudentReviewLink(eventId, studentId) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/review-link`
  );
  return response.data.url;
}

export async function requestStudentMagicLink(eventId, email) {
  const response = await publicApiClient.post(
    `/public/events/${encodeURIComponent(eventId)}/students/magic-link`,
    { email }
  );
  return response.data;
}

export async function fetchPublicStudentPronunciation(eventId, studentId, token) {
  const response = await publicApiClient.get(
    `/public/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation/${encodeURIComponent(token)}`
  );
  return response.data;
}

export async function fetchPublicStudentPronunciationAudioUrl(eventId, studentId, token) {
  const response = await publicApiClient.get(
    `/public/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation/${encodeURIComponent(token)}/audio-url`
  );
  return response.data;
}

export async function confirmPublicGeneratedPronunciation(eventId, studentId, token) {
  const response = await publicApiClient.post(
    `/public/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation/${encodeURIComponent(token)}/confirm-generated`
  );
  return response.data;
}

export async function fetchPublicPronunciationAlternatives(eventId, studentId, token, payload) {
  const response = await publicApiClient.post(
    `/public/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation/${encodeURIComponent(token)}/alternatives`,
    payload
  );
  return response.data;
}

export async function previewPublicPronunciationAlternative(eventId, studentId, token, payload) {
  const response = await publicApiClient.post(
    `/public/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation/${encodeURIComponent(token)}/alternative-preview`,
    payload
  );
  return response.data;
}

export async function submitPublicPronunciationAlternative(eventId, studentId, token, payload) {
  const response = await publicApiClient.post(
    `/public/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation/${encodeURIComponent(token)}/alternative`,
    payload
  );
  return response.data;
}

export async function submitPublicStudentRecording(eventId, studentId, token, payload) {
  const response = await publicApiClient.post(
    `/public/events/${encodeURIComponent(eventId)}/students/${encodeURIComponent(studentId)}/pronunciation/${encodeURIComponent(token)}/recording`,
    payload
  );
  return response.data;
}

export async function startPronunciationJob(eventId, payload = {}) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/pronunciations/jobs`,
    payload
  );
  return response.data;
}

export async function fetchPronunciationJob(eventId, jobId) {
  const response = await apiClient.get(
    `/events/${encodeURIComponent(eventId)}/pronunciations/jobs/${encodeURIComponent(jobId)}`
  );
  return response.data.job;
}

export async function fetchPronunciationAudioUrl(eventId, participantId) {
  const response = await apiClient.get(
    `/events/${encodeURIComponent(eventId)}/participants/${encodeURIComponent(participantId)}/pronunciation-audio-url`
  );
  return response.data;
}

export async function publishQueue(eventId, payload = {}) {
  const envelope = preparationEnvelope(payload.baseRevision);
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/queue/publish`,
    { ...payload, ...envelope }
  );
  return response.data;
}

export async function updateQueue(eventId, payload) {
  const response = await apiClient.put(
    `/events/${encodeURIComponent(eventId)}/state`,
    payload,
    { timeout: 8000 }
  );
  return response.data;
}

export async function addParticipantToQueue(
  eventId,
  participantId,
  allowDuplicate = false,
  envelope = {}
) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/queue/add-from-participant`,
    { ...envelope, participantId, allowDuplicate },
    { timeout: 8000 }
  );
  return response.data;
}

export async function nextPreview(eventId, envelope) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/queue/preview/next`,
    envelope,
    { timeout: 8000 }
  );
  return response.data;
}

export async function previousPreview(eventId, envelope) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/queue/preview/previous`,
    envelope,
    { timeout: 8000 }
  );
  return response.data;
}

export async function pushPreviewLive(eventId, envelope) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/queue/push-live`,
    envelope,
    { timeout: 8000 }
  );
  return response.data;
}

export async function undoPushPreviewLive(eventId, envelope) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/queue/push-live/undo`,
    envelope,
    { timeout: 8000 }
  );
  return response.data;
}

export async function selectPreview(eventId, index, envelope) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/queue/preview/select`,
    { ...envelope, index },
    { timeout: 8000 }
  );
  return response.data;
}

export async function restoreHistoryItem(eventId, historyId, envelope) {
  const response = await apiClient.post(
    `/events/${encodeURIComponent(eventId)}/queue/history/restore`,
    { ...envelope, historyId },
    { timeout: 8000 }
  );
  return response.data;
}
